import React, {useMemo} from 'react'
import {Box, Grid, Text, GridItem, useMultiStyleConfig, GridProps, TextProps, VStack} from '@chakra-ui/react'
import {Picture, ProductTile, ProductSkeleton, Breadcrumb} from '_uiComponents'
import type {FullProductType, Page} from '_types'
import {computeMediaItems, TILE_ASSET_SIZE} from '_utils/data/product'
import {updateCloudinarySize, getOnlineSetProducts} from '_utils'
import {useViewport} from '_customHooks'
import {useModal} from '_context/modal/ModalContext'
import ZoomImageModal from '../product-details/partials/media-carousel/partials/zoom-image-modal'
import {useIntl} from 'react-intl'

interface ShopByLookProps {
    product: FullProductType
    pageDesignerData?: Page
    pageDesignerModules?: Page
}

/**
 * @name ShopByLook
 *
 * @component
 *
 * @description
 * Skeleton PDP layout for "shop by look" product sets. Desktop shows the look's main image on
 * the left and a 2-column grid of the products that compose the look on the right; mobile/tablet
 * stack the image and a single-column product list, with a "Shop by Look" heading between them.
 *
 * @remarks
 * Data model (`product.c_setData`) is still being finalized on the backend; this is a first
 * structural pass and is expected to evolve. The look's products are re-fetched via `useProducts`
 * (same pattern as `ProductRecommendations`) so the existing `ProductTile` molecule can be reused
 * as-is, with its wishlist/quick-add behaviour, instead of the embedded `c_setData` pricing/images.
 */
const ShopByLook = React.memo(({product}: ShopByLookProps) => {
    const styles = useMultiStyleConfig('ShopByLook') as Record<string, GridProps & TextProps>
    const setData = product.c_setData
    const {isTablet, isDesktop} = useViewport()
    const {openModal} = useModal()
    const {formatMessage} = useIntl()

    const PAGE_TITLE = formatMessage({id: 'label.pdp.shopbylook.page.title', defaultMessage: 'Shop by Look'})

    // Reuse the canonical category list from the product service (`c_analytics.list`, e.g.
    // `category-man-how_to_style-aperitif`) and swap only its context prefix to `pdp-`, matching
    // the pattern used by PDP recommendations (`relatives-`/`einstein-`). The old
    // `c_setData.analytics.list` no longer exists after the backend normalized the set payload.
    const analyticsList = product?.c_analytics?.list?.replace(/^category-/, 'pdp-') ?? ''

    // Render tiles only from the online, resolved members: members with `online === false` or that
    // are missing/unresolved are filtered out so they do not produce an empty tile. While the set
    // data is still loading (`c_setData` undefined) `lookProducts` stays `undefined` → skeletons.
    const lookProducts = setData ? getOnlineSetProducts(product) : undefined

    const activeMediaItems = useMemo(
        () => computeMediaItems({images: setData?.images, isDesktop, isTablet, type: 'shopbylook'}),
        [setData?.images, isDesktop, isTablet],
    )

    const lookImage = setData?.images?.large?.[0]
    const lookImageSrc = useMemo(
        () =>
            lookImage?.url
                ? {
                      imagesm: updateCloudinarySize(lookImage.url, TILE_ASSET_SIZE.mobile.shopbylook),
                      imagemd: updateCloudinarySize(lookImage.url, TILE_ASSET_SIZE.tablet.shopbylook),
                      imagelg: updateCloudinarySize(lookImage.url, TILE_ASSET_SIZE.desktop.shopbylook),
                  }
                : undefined,
        [lookImage?.url],
    )

    const breadcrumbItems = useMemo(() => {
        const itemListElement = (product?.c_seo?.schemaBreadcrumbs as {itemListElement?: Array<{item?: string; name?: string}>} | undefined)?.itemListElement

        if (!itemListElement || !Array.isArray(itemListElement)) {
            return []
        }

        // The last breadcrumb item is the leaf product name; drop it from the visible breadcrumb
        // (same behaviour as the PDP organism), keeping only the category ancestry.
        return itemListElement
            .filter((item) => item?.name)
            .slice(0, -1)
            .map((item, index) => ({
                id: String(index),
                htmlValue: item.name || '',
                url: item.item || '',
            }))
    }, [product])

    const openZoomModal = () => {
        document.body.style.overflow = 'hidden'
        openModal({
            body: <ZoomImageModal mediaItems={activeMediaItems} initialIndex={0} productName={product?.c_shortDescription ?? ''} />,
            variant: 'mediaZoom',
            className: 'cc-zoom-modal',
            closingIcon: 'IconCloseLight',
            closingIconSize: 'lg',
            onClose: () => {
                document.body.style.overflow = ''
            },
        })
    }

    return (
        <Grid {...styles.container} data-testid="shop-by-look">
            <VStack {...styles.breadCrumbContainer}>{breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} variant="default" />}</VStack>
            <Box {...styles.imageContainer}>
                {lookImageSrc && (
                    <Picture
                        {...lookImageSrc}
                        alt={lookImage?.alt}
                        onClick={() => openZoomModal()}
                        width="100%"
                        objectFit="cover"
                        positionContainer="sticky"
                        top="0"
                        height={'auto'}
                        // The look's hero image is above-the-fold (LCP candidate), so it must load eagerly instead of lazily.
                        hasLazy={false}
                        fetchpriority="high"
                    />
                )}
            </Box>
            <Text as="h2" {...styles.title}>
                {PAGE_TITLE}
            </Text>
            <Grid {...styles.tilesContainer}>
                {!lookProducts
                    ? Array.from({length: 4}, (_, index) => (
                          <GridItem key={`shop-by-look-skeleton-${index}`} minW={0}>
                              <ProductSkeleton />
                          </GridItem>
                      ))
                    : lookProducts.map((tileProduct, idx) => (
                          <GridItem key={tileProduct.productId ?? tileProduct.id} minW={0}>
                              <ProductTile product={tileProduct} idx={idx} listingLayout="grid2" analyticsList={analyticsList} />
                          </GridItem>
                      ))}
            </Grid>
        </Grid>
    )
})

ShopByLook.displayName = 'ShopByLook'

export {ShopByLook}
